import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  ACCESS_MENUS,
  AccessRoleDef,
  emptyPermissions,
  builtinAccessRoles,
} from "@/lib/staff-permissions";
import { ShieldCheck, Plus, Trash2, Save, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";

interface Props {
  roles: AccessRoleDef[];
  saveRoles: (roles: AccessRoleDef[]) => Promise<any>;
}

const AccessRolesManager = ({ roles, saveRoles }: Props) => {
  const { toast } = useToast();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const update = async (updated: AccessRoleDef[], message: string) => {
    setSaving(true);
    const error = await saveRoles(updated);
    setSaving(false);
    toast(
      error
        ? { title: "Erro ao guardar", description: error.message, variant: "destructive" }
        : { title: message }
    );
  };

  const patchRole = (key: string, patch: Partial<AccessRoleDef>) =>
    roles.map((r) => (r.key === key ? { ...r, ...patch } : r));

  const addRole = async () => {
    let base = "nova_categoria";
    let key = base;
    let i = 2;
    while (roles.some((r) => r.key === key)) key = `${base}_${i++}`;
    const nova: AccessRoleDef = {
      key,
      label: "Nova categoria",
      description: "",
      permissions: emptyPermissions(),
    };
    await update([...roles, nova], "Categoria de acesso criada");
    setOpenKey(key);
  };

  const removeRole = async (role: AccessRoleDef) => {
    if (role.key === "admin") {
      toast({ title: "A categoria Administrador não pode ser removida", variant: "destructive" });
      return;
    }
    if (!confirm(`Remover a categoria "${role.label}"? Os colaboradores nesta categoria mantêm as permissões atuais.`))
      return;
    await update(roles.filter((r) => r.key !== role.key), "Categoria removida");
  };

  const togglePerm = (role: AccessRoleDef, permKey: string, value: boolean) => {
    if (role.key === "admin") {
      toast({ title: "A categoria Administrador tem sempre acesso total" });
      return;
    }
    update(
      patchRole(role.key, { permissions: { ...role.permissions, [permKey]: value } }),
      "Permissões atualizadas"
    );
  };

  const toggleMenu = (role: AccessRoleDef, menuKey: string, value: boolean) => {
    if (role.key === "admin") {
      toast({ title: "A categoria Administrador tem sempre acesso total" });
      return;
    }
    const menu = ACCESS_MENUS.find((m) => m.key === menuKey);
    if (!menu) return;
    const perms = { ...role.permissions };
    menu.submenus.forEach((s) => (perms[s.key] = value));
    update(patchRole(role.key, { permissions: perms }), "Permissões atualizadas");
  };

  const resetDefaults = async () => {
    if (!confirm("Repor as categorias originais (Administrador, Gerente, Financeiro, Caixa, Colaborador)?")) return;
    await update(builtinAccessRoles(), "Categorias repostas");
  };


  const countActive = (role: AccessRoleDef) =>
    Object.values(role.permissions || {}).filter(Boolean).length;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-primary" />
          <div>
            <h2 className="font-display text-lg font-semibold">Categorias de Acesso</h2>
            <p className="text-xs text-muted-foreground">
              Defina que menus e submenus do sistema cada categoria pode aceder.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="gap-1" onClick={resetDefaults} disabled={saving}>
            <RotateCcw size={14} /> Repor
          </Button>
          <Button size="sm" className="gap-1" onClick={addRole} disabled={saving}>
            <Plus size={14} /> Nova categoria
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {roles.map((role) => {
          const open = openKey === role.key;
          return (
            <div key={role.key} className="rounded-xl border border-border bg-background overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenKey(open ? null : role.key)}
                className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{role.label}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {role.description || "Sem descrição"}
                    </p>
                  </div>
                </div>
                <span className="text-[11px] shrink-0 bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  {countActive(role)} acessos
                </span>
              </button>

              {open && (
                <div className="border-t border-border p-4 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome da categoria</Label>
                      <Input
                        value={role.label}
                        onChange={(e) =>
                          saveRoles(patchRole(role.key, { label: e.target.value }))
                        }
                        onBlur={() => toast({ title: "Categoria guardada" })}
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Identificador</Label>
                      <Input value={role.key} disabled className="h-9 opacity-70" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Descrição</Label>
                    <Textarea
                      value={role.description}
                      onChange={(e) =>
                        saveRoles(patchRole(role.key, { description: e.target.value }))
                      }
                      onBlur={() => toast({ title: "Categoria guardada" })}
                      rows={2}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Menus e submenus
                    </Label>
                    {ACCESS_MENUS.map((menu) => {
                      const total = menu.submenus.length;
                      const active = menu.submenus.filter((s) => role.permissions?.[s.key]).length;
                      const expanded = expandedMenus[`${role.key}:${menu.key}`] ?? true;
                      return (
                        <div key={menu.key} className="rounded-xl border border-border">
                          <div className="flex items-center justify-between gap-3 p-3 bg-muted/40">
                            <button
                              type="button"
                              className="flex items-center gap-2 text-sm font-medium"
                              onClick={() =>
                                setExpandedMenus((p) => ({
                                  ...p,
                                  [`${role.key}:${menu.key}`]: !expanded,
                                }))
                              }
                            >
                              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {menu.label}
                              <span className="text-[11px] text-muted-foreground font-normal">
                                ({active}/{total})
                              </span>
                            </button>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-muted-foreground">Todo o menu</span>
                              <Switch
                                checked={active === total}
                                onCheckedChange={(v) => toggleMenu(role, menu.key, v)}
                              />
                            </div>
                          </div>
                          {expanded && (
                            <div className="p-3 grid sm:grid-cols-2 gap-2">
                              {menu.submenus.map((sub) => (
                                <label
                                  key={sub.key}
                                  className="flex items-start gap-3 rounded-xl border border-border bg-background p-3 cursor-pointer hover:bg-muted/50"
                                >
                                  <Checkbox
                                    checked={!!role.permissions?.[sub.key]}
                                    onCheckedChange={(v) => togglePerm(role, sub.key, !!v)}
                                    className="mt-0.5"
                                  />
                                  <span>
                                    <span className="text-sm block">{sub.label}</span>
                                    <span className="text-[11px] text-muted-foreground">{sub.hint}</span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-between pt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="gap-1 text-destructive hover:text-destructive"
                      onClick={() => removeRole(role)}
                    >
                      <Trash2 size={14} /> Remover categoria
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1"
                      disabled={saving}
                      onClick={() => update(roles, "Categorias guardadas")}
                    >
                      <Save size={14} /> Guardar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        As categorias definidas aqui ficam disponíveis na ficha de cada colaborador (Acesso e Permissões).
      </p>
    </div>
  );
};

export default AccessRolesManager;
